import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, IconButton, Dialog, DialogContent, Tabs, Tab, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import DownloadIcon from '@mui/icons-material/Download';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxIcon from '@mui/icons-material/AddBox';
import CheckIcon from '@mui/icons-material/Check';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useInstalacion } from '../hooks/useInstalacion';
import { useNotification } from '../hooks/useNotification';

// Mismo patrón ya probado en otro proyecto (CorrerPro): banner en el home
// que se puede cerrar (reaparece después de un tiempo) + acceso permanente
// desde algún lugar fijo (acá: Layout.js) para volver a ver la guía cuando
// quieran. Pedido explícito del usuario 2026-10-16: que quien abra
// VitaNotas desde el celular tenga la guía de instalación a mano.
const CERRADO_KEY = 'vitanotas_instalar_cerrado_hasta';
const DIAS_CERRADO = 21;

const Boton = ({ children }) => (
  <Box component="b" sx={{
    display: 'inline-block', px: 0.75, py: '1px', borderRadius: 0.75,
    bgcolor: 'action.selected', fontWeight: 700, whiteSpace: 'nowrap',
  }}>
    {children}
  </Box>
);

function pasosIphone(navegador) {
  const dondeCompartir = navegador === 'safari'
    ? 'Está en la barra de abajo.'
    : 'En Safari está abajo; en Chrome, arriba en la barra de la dirección.';
  return [
    { icono: <IosShareIcon fontSize="small" />, titulo: <>Tocá <Boton>Compartir</Boton></>,
      detalle: <>Es el cuadrado con la flecha para arriba. {dondeCompartir}</> },
    { icono: <AddBoxIcon fontSize="small" />, titulo: <>Elegí <Boton>Agregar a inicio</Boton></>,
      detalle: 'Bajá un poco en el menú si no aparece. En algunos iPhone dice "Agregar a pantalla de inicio".' },
    { icono: <CheckIcon fontSize="small" />, titulo: <>Tocá <Boton>Agregar</Boton></>,
      detalle: 'Arriba a la derecha.' },
    { icono: <SmartphoneIcon fontSize="small" />, titulo: 'Abrila desde el ícono nuevo',
      detalle: 'Buscá el ícono de VitaNotas en tu pantalla de inicio. La primera vez te pide iniciar sesión de nuevo.' },
  ];
}

function pasosAndroid(navegador) {
  const samsung = navegador === 'samsung';
  return [
    { icono: <MoreVertIcon fontSize="small" />, titulo: <>Abrí el menú <Boton>{samsung ? '≡' : '⋮'}</Boton></>,
      detalle: samsung ? 'Está en la barra de abajo de Samsung Internet.' : 'Arriba a la derecha de Chrome.' },
    { icono: <DownloadIcon fontSize="small" />, titulo: <>Tocá <Boton>Instalar app</Boton></>,
      detalle: samsung
        ? 'En Samsung Internet: "Agregar página a" → "Pantalla de inicio".'
        : 'Según el celular puede decir "Agregar a la pantalla principal".' },
    { icono: <CheckIcon fontSize="small" />, titulo: <>Confirmá con <Boton>Instalar</Boton></> },
    { icono: <SmartphoneIcon fontSize="small" />, titulo: 'Abrila desde el ícono nuevo',
      detalle: 'Aparece con el resto de tus apps, como VitaNotas.' },
  ];
}

export function GuiaInstalacionDialog({ open, onClose }) {
  const inst = useInstalacion();
  const disp = inst.dispositivo;
  const [sistema, setSistema] = useState('ios');
  const notify = useNotification();

  useEffect(() => { if (disp?.so === 'android') setSistema('android'); }, [disp?.so]);

  const navegador = disp && disp.so === sistema ? disp.navegador : null;
  const pasos = sistema === 'ios' ? pasosIphone(navegador) : pasosAndroid(navegador);
  const enOtraApp = disp?.navegador === 'app';
  const enCompu = disp && !disp.celular;

  const handleInstalar = async () => {
    if (await inst.instalar()) notify('¡Listo! Abrila desde el ícono de VitaNotas', 'success');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ pt: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box>
            <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              En 4 pasos
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
              Instalá VitaNotas en tu celular
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Cerrar">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {enOtraApp && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Estás viendo VitaNotas adentro de otra app (Instagram, WhatsApp…) y desde ahí no se puede
            instalar. Tocá el menú (··· o ⋮) y elegí "Abrir en {disp?.so === 'ios' ? 'Safari' : 'Chrome'}".
          </Alert>
        )}

        {enCompu && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Estás en una computadora: entrá a esta misma página desde el celular, iniciá sesión y seguí estos pasos.
          </Alert>
        )}

        <Tabs
          value={sistema}
          onChange={(_, v) => setSistema(v)}
          variant="fullWidth"
          sx={{ mb: 2, minHeight: 36, border: '1px solid', borderColor: 'divider', borderRadius: 1, '& .MuiTab-root': { minHeight: 36 } }}
        >
          <Tab value="ios" label="iPhone" />
          <Tab value="android" label="Android" />
        </Tabs>

        {sistema === 'android' && inst.puedeInstalarDirecto && !enCompu && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 2, p: 1.25, borderRadius: 1, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.light' }}>
            <Typography variant="body2">Tu celular la puede instalar con un toque:</Typography>
            <Button size="small" variant="contained" startIcon={<DownloadIcon fontSize="small" />} onClick={handleInstalar} sx={{ flexShrink: 0 }}>
              Instalar
            </Button>
          </Box>
        )}

        <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          {pasos.map((p, i) => (
            <Box component="li" key={i} sx={{ display: 'flex', gap: 1.5 }}>
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: 1.5, bgcolor: 'action.hover',
                  border: '1px solid', borderColor: 'divider',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {p.icono}
                </Box>
                <Box sx={{
                  position: 'absolute', top: -6, left: -6, width: 18, height: 18, borderRadius: '50%',
                  bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: '0.68rem', fontWeight: 800,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {i + 1}
                </Box>
              </Box>
              <Box sx={{ pt: 0.25, minWidth: 0 }}>
                <Typography variant="body2" sx={{ lineHeight: 1.4 }}>{p.titulo}</Typography>
                {p.detalle && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}>{p.detalle}</Typography>}
              </Box>
            </Box>
          ))}
        </Box>

        {enCompu && inst.puedeInstalarDirecto && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">¿La querés también en esta compu?</Typography>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon fontSize="small" />} onClick={handleInstalar}>
              Instalar
            </Button>
          </Box>
        )}

        <Button fullWidth variant="outlined" onClick={onClose} sx={{ mt: 2.5 }}>
          Listo
        </Button>
      </DialogContent>
    </Dialog>
  );
}

// Banner para el home — solo en celular, solo si no está ya instalada/abierta
// como app, y se puede cerrar (vuelve a aparecer después de unos días).
export function BannerInstalarApp() {
  const inst = useInstalacion();
  const [cerrado, setCerrado] = useState(true);
  const [guiaOpen, setGuiaOpen] = useState(false);
  const notify = useNotification();

  useEffect(() => {
    try {
      const hasta = Number(localStorage.getItem(CERRADO_KEY) || 0);
      setCerrado(hasta > Date.now());
    } catch { setCerrado(false); }
  }, []);

  if (!inst.dispositivo?.celular || inst.abiertaComoApp || cerrado) return null;

  const cerrar = () => {
    try { localStorage.setItem(CERRADO_KEY, String(Date.now() + DIAS_CERRADO * 24 * 60 * 60 * 1000)); } catch { /* solo por ahora */ }
    setCerrado(true);
  };

  const handleInstalar = async () => {
    if (await inst.instalar()) notify('¡Listo! Abrila desde el ícono de VitaNotas', 'success');
  };

  return (
    <>
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5, mb: 2,
        borderRadius: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.light',
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5, bgcolor: 'primary.main', color: 'primary.contrastText',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <SmartphoneIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700}>Instalá la app en tu celular</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            Queda con su ícono y se abre a pantalla completa, como cualquier app. Es gratis y no ocupa lugar.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            {inst.puedeInstalarDirecto ? (
              <Button size="small" variant="contained" startIcon={<DownloadIcon fontSize="small" />} onClick={handleInstalar}>
                Instalar
              </Button>
            ) : (
              <Button size="small" variant="contained" onClick={() => setGuiaOpen(true)}>
                Ver cómo (1 minuto)
              </Button>
            )}
            <Button size="small" onClick={cerrar}>Ahora no</Button>
          </Box>
        </Box>
        <IconButton size="small" onClick={cerrar} sx={{ flexShrink: 0 }} aria-label="Cerrar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <GuiaInstalacionDialog open={guiaOpen} onClose={() => setGuiaOpen(false)} />
    </>
  );
}
